import { GaugeVisualState } from "@atiautomacao/ati-ui-library";

// Prefixo do título de cada gauge (SGD-3683 RN2c: "BMSXX").
export const BMS_LABEL_PREFIX = "BMS";

/**
 * Número do rack formatado com dois dígitos, a partir da referência do teleobjeto (RN2c).
 * Ex.: 1 -> "01", 12 -> "12". Sem referência -> "--".
 */
export const formatBmsNumber = (reference: number | null): string => {
    if (reference == null) return "--";
    return String(reference).padStart(2, "0");
};

/**
 * Nome do estado a partir do status. O backend envia "Normalizado" / "Alarmado" / "Sem dados";
 * também tolera o padrão "<Nome> - <HEX>" (usado na legenda).
 */
const statusName = (status: string): string => {
    if (!status) return "";
    return status.includes(" - ") ? status.split(" - ")[0].trim() : status.trim();
};

/**
 * Mapeia o status do backend para o visualState do AStringCurrentGauge.
 * Precedência de estado já vem resolvida no backend (Sem dados > Alarmado > Normalizado).
 */
export const resolveGaugeVisualState = (status: string): GaugeVisualState => {
    switch (statusName(status)) {
        case "Alarmado":
            return "alarm";
        case "Sem dados":
            return "stale";
        default:
            return "normal";
    }
};

/**
 * Fração de preenchimento do gauge (0..1) a partir do SOC (%). Sem valor -> 0 (gauge vazio).
 */
export const resolveFillRatio = (socValue: number | null): number => {
    if (socValue == null) return 0;
    const ratio = socValue / 100;
    if (ratio < 0) return 0;
    if (ratio > 1) return 1;
    return ratio;
};

/**
 * Subtítulo dinâmico da seção (RN1.b.iii / RN4b): contagem + range das referências.
 * Ex.: "16 racks monitorados | BMS01 - BMS16"; sem racks -> "0 racks monitorados".
 */
export const buildBmsSummary = (racks: Array<{ reference: number | null }>): string => {
    const count = racks.length;
    const noun = count === 1 ? "rack monitorado" : "racks monitorados";
    const summary = `${count} ${noun}`;

    const references = racks
        .map((rack) => rack.reference)
        .filter((reference): reference is number => reference != null);
    if (references.length === 0) return summary;

    const min = Math.min(...references);
    const max = Math.max(...references);
    const range =
        min === max
            ? `${BMS_LABEL_PREFIX}${formatBmsNumber(min)}`
            : `${BMS_LABEL_PREFIX}${formatBmsNumber(min)} - ${BMS_LABEL_PREFIX}${formatBmsNumber(max)}`;
    return `${summary} | ${range}`;
};

// Itens da legenda (RN1.b.ii): Normalizado / Alarmado / Sem dados. Cores no padrão "<Nome> - <HEX>"
// resolvidas por getIconColor — mesmas cores usadas na seção de PCS.
export const BMS_LEGEND_ITEMS: Array<{ label: string; status: string }> = [
    { label: "Normalizado", status: "Normalizado - 1BB483" },
    { label: "Alarmado", status: "Alarmado - D32F2F" },
    { label: "Sem dados", status: "Sem dados - 0000003B" },
];
